import * as React from 'react'

import {
  FolderOpenIcon,
  MailIcon,
  MapPinIcon,
  SettingsIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IProject, ISharedDelivery } from '~/client/graph'
import * as Icons from '~/client/src/shared/components/Icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NO_SPECIFIED } from '~/client/src/shared/utils/usefulStrings'

import Delivery from '../../models/Delivery'
import { getSharedProjectName } from '../../models/SharedInfo'
import ProjectsStore from '../../stores/domain/Projects.store'
import InitialState from '../../stores/InitialState'
import DeliveryDateLabel from '../DeliveryDateLabel/DeliveryDateLabel'
import DeliveryStatusLabel from '../DeliveryStatusLabel/DeliveryStatusLabel'
import {
  ISharedAction,
  isPhoneValid,
  renderActionItem,
  SharedEntityActions,
  SharedEntityActionTypes,
} from './SharedEntityActions'

import './SharedEntityActions.scss'

interface IProps {
  entity: ISharedDelivery
  projectId: string
  companyName: string
  onActionClick: (
    actionType: SharedEntityActionTypes,
    sharedEntity: ISharedDelivery,
    e: React.MouseEvent,
  ) => void
  isMobile?: boolean
  projectsStore?: ProjectsStore
  state?: InitialState
}

// eslint-disable-next-line
@inject('projectsStore', 'state')
@observer
export default class SharedDeliveryActions extends React.Component<IProps> {
  private get delivery(): Delivery {
    return Delivery.fromSharedDeliveryDto(this.props.entity)
  }

  private get project(): IProject {
    const { projectsStore, projectId } = this.props
    return projectsStore.getProjectById(projectId)
  }

  public render(): JSX.Element {
    const {
      entity,
      projectId,
      companyName,
      onActionClick,
      isMobile,
      projectsStore,
      state,
    } = this.props

    const projectName =
      this.project?.name ||
      getSharedProjectName(
        entity.projectUrl,
        projectsStore,
        entity.projectFromId,
      )

    return (
      <SharedEntityActions
        entity={entity}
        projectId={projectId}
        companyName={companyName}
        onActionClick={onActionClick}
        isMobile={isMobile}
        projectsStore={projectsStore}
        state={state}
        renderActions={this.renderActions}
        renderInfo={this.renderInfo}
        projectName={projectName}
      />
    )
  }

  private renderActions = (): JSX.Element => {
    const { entity: delivery, isMobile, state, onActionClick } = this.props
    const { isAdmin } = state.userActiveProjectSettings

    const actions: ISharedAction[] = [
      {
        type: SharedEntityActionTypes.GO_TO_PROJECT,
        label: Localization.translator.goToTheProject,
        icon: <FolderOpenIcon size={20} className="tw:text-muted-foreground" />,
      },
    ]

    if (delivery.locationsTo?.[0]?.locationUrl) {
      actions.push({
        type: SharedEntityActionTypes.GO_TO_TO_LOCATION,
        label: Localization.translator.goToLocation(
          delivery.locationsTo[0]?.locationName,
        ),
        icon: <MapPinIcon size={16} />,
      })
    }

    if (delivery.locationsFrom?.[0]?.locationUrl) {
      actions.push({
        type: SharedEntityActionTypes.GO_TO_FROM_LOCATION,
        label: Localization.translator.goToLocation(
          delivery.locationsFrom[0]?.locationName,
        ),
        icon: <MapPinIcon size={16} />,
      })
    }

    actions.push(
      {
        type: SharedEntityActionTypes.CALL_ADMIN,
        label: Localization.translator.callTo(delivery.responsible.firstName),
        icon: <Icons.SharedEntityCall />,
        disabled: !isPhoneValid(delivery.responsible.phoneNumber),
      },
      {
        type: SharedEntityActionTypes.MAIL_ADMIN,
        label: Localization.translator.mailTo(delivery.responsible.firstName),
        icon: <MailIcon size={20} className="tw:text-muted-foreground" />,
        disabled: !delivery.responsible.email,
      },
    )

    if (!isMobile && isAdmin) {
      actions.push({
        type: SharedEntityActionTypes.GO_TO_SETTINGS,
        label: Localization.translator.iCalSharingSettings,
        icon: <SettingsIcon size={20} className="tw:text-muted-foreground" />,
      })
    }

    return (
      <div className="shared-actions-list col">
        {actions.map(action =>
          renderActionItem(action, onActionClick, delivery),
        )}
      </div>
    )
  }

  private renderInfo = (): JSX.Element => {
    const { entity: delivery, companyName } = this.props
    return (
      <div className="delivery-info row pa10 y-start">
        <div className="col y-start no-grow">
          <div className="delivery-workflow-card-icon bg-dark br-rounded text white row x-center y-center no-flex mr20">
            <Icons.DeliveryFilled className="no-grow filled-delivery-icon" />
          </div>
        </div>
        <div className="col">
          <div className="row y-stretch">
            <span className="col row delivery-company-title text extra-large bold ellipsis">
              {companyName}
              <span className="col ml5 text light pa2 pt4">
                #{delivery.bookingId}
              </span>
            </span>
            <DeliveryStatusLabel
              status={delivery.status}
              className="col mx10 no-grow"
            />
          </div>
          <div className="row x-between mb4">
            <DeliveryDateLabel
              delivery={this.delivery}
              className="text-ellipsis mr4"
            />
          </div>
          <div className="row wrap y-center mb4">
            <div className="row no-compressed-text">
              <Icons.SharedEntity className="no-grow mr5 shared-entity-icon" />
              <div className="text bold large underline">
                {`${Localization.translator.sharedFrom} ${
                  this.project?.name ||
                  getSharedProjectName(delivery.projectUrl)
                }`}
              </div>
            </div>
            {delivery.locationsTo?.[0] && (
              <>
                <Icons.SharedEntityDotSeparator className="no-grow shared-entity-icon" />
                <div className="row no-compressed-text">
                  <Icons.SharedEntityOfficeLabel className="no-grow mr5 shared-entity-icon" />
                  <div className="text bold large underline no-compressed-text">
                    {delivery.locationsTo[0].locationName}
                  </div>
                </div>
              </>
            )}
            <Icons.SharedEntityDotSeparator className="no-grow shared-entity-icon" />
            <div className="row no-compressed-text">
              <Icons.SharedEntityUser className="no-grow shared-entity-icon mr5" />
              <div className="text bold large underline no-compressed-text">
                {delivery.responsible?.firstName || NO_SPECIFIED}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
